<script setup lang="ts">
/** Shares & links: the caller's password/sign-in protected shares and short links — copy, create, revoke. */
import { Ban, Link2, Plus, Share2 } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, h, ref } from "vue";
import { toast } from "vue-sonner";
import { createLink, createShare, revokeLink, revokeShare, useLinks, useShares, type NewShare, type Share, type ShareAudience, type ShareFormat, type ShortLink } from "@/api/shares";
import CopyButton from "@/components/common/CopyButton.vue";
import CopyField from "@/components/common/CopyField.vue";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FormError from "@/components/common/FormError.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs from "@/components/common/PageTabs.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { shares as c } from "@/copy/shares";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import type { Column } from "@/lib/table";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Shares & links", requires: { capability: "share.read" } } });

const tab = useRouteQuery("tab", "shares");
const queryClient = useQueryClient();
const { can } = useSession();
const canWrite = computed(() => can("share.write"));
const shareList = useShares();
const links = useLinks();
const filter = ref("");
const tabs = computed(() => [
  { id: "shares", label: c.tabShares, badge: shareList.data.value?.filter((s) => s.status === "active").length },
  { id: "links", label: c.tabLinks, badge: links.data.value?.items.filter((l) => l.status === "active").length },
]);

const STATUS_VARIANT: Record<string, "success" | "muted" | "danger" | "warning"> = { active: "success", expired: "muted", revoked: "danger", used_up: "warning" };
const statusBadge = (status: string) => h(Badge, { variant: STATUS_VARIANT[status] ?? "muted" }, () => c.status[status] ?? status);
// Table cells never wrap and `truncate` alone does not limit a column, so links get
// an explicit cap from sm up, where DataTable shows a table (full URL in the tooltip
// and the copy button). The caps keep both tables, Revoke included, inside the frame
// down to 1152px (1440 at 125%). Phone cards truncate at the card's width.
const linkCell = (url: string, active: boolean, cap: string) =>
  h("div", { class: "flex min-w-0 items-center gap-1" }, [
    h(
      "a",
      { href: url, target: "_blank", rel: "noreferrer", title: url, class: cn("block truncate font-mono text-xs hover:underline", cap, !active && "text-muted-foreground line-through") },
      url.replace(/^https?:\/\//, ""),
    ),
    active ? h(CopyButton, { value: url, label: "Copy link", class: "-my-1.5" }) : null,
  ]);

// --- revoke

async function onRevokeShare(s: Share) {
  const answer = await confirm({ title: `${c.revoke} "${s.title}"?`, description: c.revokeShareConfirm(s.title), confirmLabel: c.revoke, destructive: true });
  if (!answer) return;
  try {
    await revokeShare(s.id);
    toast.success(c.revoked);
    await queryClient.invalidateQueries({ queryKey: ["shares"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function onRevokeLink(l: ShortLink) {
  const answer = await confirm({ title: `${c.revoke} ${l.shortUrl}?`, description: c.revokeLinkConfirm(l.shortUrl), confirmLabel: c.revoke, destructive: true });
  if (!answer) return;
  try {
    await revokeLink(l.id);
    toast.success(c.revoked);
    await queryClient.invalidateQueries({ queryKey: ["shares", "links"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const revokeButton = (active: boolean, onClick: () => void, label: string) =>
  canWrite.value && active
    ? h(Button, { size: "sm", variant: "ghost", class: "text-destructive", "aria-label": label, onClick: (e: Event) => (e.stopPropagation(), onClick()) }, () => [h(Ban), c.revoke])
    : null;

// --- tables

const shareColumns = computed<Column<Share>[]>(() => [
  {
    id: "title",
    header: c.columns.title,
    accessorFn: (s) => `${s.title} ${s.fileName ?? ""} ${s.url}`,
    meta: { mobile: "title" },
    // The share link sits under the title instead of in a column of its own: a
    // random slug says little, and the freed width keeps Expires, Views and Revoke
    // on screen at 1152px.
    cell: ({ row }) =>
      h("div", { class: "flex max-w-md flex-col gap-0.5 whitespace-normal" }, [
        h("p", { class: "font-medium [overflow-wrap:anywhere]" }, row.original.title),
        h(
          "p",
          { class: "text-xs text-muted-foreground [overflow-wrap:anywhere]" },
          row.original.kind === "file" ? (row.original.fileName ?? row.original.mimeType) : (c.format[row.original.kind] ?? row.original.kind),
        ),
        linkCell(row.original.url, row.original.status === "active", "sm:max-w-48 xl:max-w-64"),
      ]),
  },
  { id: "audience", header: c.columns.audience, accessorFn: (s) => c.audience[s.audience], cell: ({ row }) => h("span", { class: "text-sm whitespace-nowrap" }, c.audience[row.original.audience] ?? row.original.audience) },
  { id: "status", header: c.columns.status, accessorFn: (s) => s.status, meta: { mobile: "badge" }, cell: ({ row }) => statusBadge(row.original.status) },
  { id: "expires", header: c.columns.expires, accessorFn: (s) => s.expiresAt, cell: ({ row }) => h(RelativeTime, { value: row.original.expiresAt, class: "text-sm whitespace-nowrap" }) },
  {
    id: "views",
    header: c.columns.views,
    accessorFn: (s) => s.views,
    meta: { align: "end" },
    cell: ({ row }) => h("span", { class: "tabular-nums text-sm" }, row.original.maxViews ? `${row.original.views} / ${row.original.maxViews}` : String(row.original.views)),
  },
  { id: "actions", header: "", enableSorting: false, meta: { mobile: "actions", align: "end" }, cell: ({ row }) => revokeButton(row.original.status === "active", () => onRevokeShare(row.original), `${c.revoke} ${row.original.title}`) },
]);

const linkColumns = computed<Column<ShortLink>[]>(() => [
  {
    id: "short",
    header: c.columns.link,
    accessorFn: (l) => `${l.shortUrl} ${l.title ?? ""}`,
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "flex max-w-xs flex-col gap-0.5 whitespace-normal" }, [
        // Short-link slugs are often words, so the short link gets more room than the target.
        linkCell(row.original.shortUrl, row.original.status === "active", "sm:max-w-56 xl:max-w-64"),
        row.original.title ? h("p", { class: "text-xs text-muted-foreground [overflow-wrap:anywhere]" }, row.original.title) : null,
      ]),
  },
  {
    id: "target",
    header: c.columns.target,
    accessorFn: (l) => l.url,
    cell: ({ row }) => h("span", { class: "block truncate font-mono text-xs sm:max-w-40 xl:max-w-64 2xl:max-w-sm", title: row.original.url }, row.original.url),
  },
  { id: "status", header: c.columns.status, accessorFn: (l) => l.status, meta: { mobile: "badge" }, cell: ({ row }) => statusBadge(row.original.status) },
  { id: "clicks", header: c.columns.clicks, accessorFn: (l) => l.clicks, meta: { align: "end" }, cell: ({ row }) => h("span", { class: "tabular-nums text-sm" }, String(row.original.clicks)) },
  {
    id: "expires",
    header: c.columns.expires,
    accessorFn: (l) => l.expiresAt ?? "9999",
    cell: ({ row }) => (row.original.expiresAt ? h(RelativeTime, { value: row.original.expiresAt, class: "text-sm whitespace-nowrap" }) : h("span", { class: "text-sm text-muted-foreground" }, c.never)),
  },
  { id: "actions", header: "", enableSorting: false, meta: { mobile: "actions", align: "end" }, cell: ({ row }) => revokeButton(row.original.status === "active", () => onRevokeLink(row.original), `${c.revoke} ${row.original.shortUrl}`) },
]);

// --- new share

const shareOpen = ref(false);
const shareForm = ref({ title: "", content: "", format: "markdown" as ShareFormat, audience: "password" as ShareAudience, password: "", expiresInDays: "7", maxViews: "" });
const shareError = ref<unknown>(null);
const busy = ref(false);
const created = ref<{ share: Share; password: string | null } | null>(null);
const createdOpen = computed({ get: () => Boolean(created.value), set: (v) => !v && (created.value = null) });

function openShare() {
  shareForm.value = { title: "", content: "", format: "markdown", audience: "password", password: "", expiresInDays: "7", maxViews: "" };
  shareError.value = null;
  shareOpen.value = true;
}

async function submitShare() {
  busy.value = true;
  shareError.value = null;
  const f = shareForm.value;
  const body: NewShare = { title: f.title.trim(), content: f.content, format: f.format, audience: f.audience };
  if (f.audience !== "smake" && f.password) body.password = f.password;
  if (f.expiresInDays) body.expiresInDays = Number(f.expiresInDays);
  if (f.maxViews) body.maxViews = Number(f.maxViews);
  try {
    created.value = await createShare(body);
    shareOpen.value = false;
    toast.success(c.created);
    await queryClient.invalidateQueries({ queryKey: ["shares"] });
  } catch (err) {
    shareError.value = err;
  } finally {
    busy.value = false;
  }
}

// --- new link

const linkOpen = ref(false);
const linkForm = ref({ url: "", slug: "", title: "", expiresInDays: "" });
const linkError = ref<unknown>(null);

function openLink() {
  linkForm.value = { url: "", slug: "", title: "", expiresInDays: "" };
  linkError.value = null;
  linkOpen.value = true;
}

async function submitLink() {
  busy.value = true;
  linkError.value = null;
  const f = linkForm.value;
  try {
    const link = await createLink({
      url: f.url.trim(),
      slug: f.slug.trim() || undefined,
      title: f.title.trim() || undefined,
      expiresInDays: f.expiresInDays ? Number(f.expiresInDays) : undefined,
    });
    linkOpen.value = false;
    toast.success(`${c.linkCreated} ${link.shortUrl}`);
    await queryClient.invalidateQueries({ queryKey: ["shares", "links"] });
  } catch (err) {
    linkError.value = err;
  } finally {
    busy.value = false;
  }
}

const formatOptions = Object.entries(c.format).map(([value, label]) => ({ value, label }));
const audienceOptions = Object.entries(c.audience).map(([value, label]) => ({ value, label, hint: c.audienceHint[value] }));
</script>

<template>
  <div>
    <PageHeader :title="c.title" :description="c.description">
      <template v-if="canWrite" #actions>
        <Button v-if="tab === 'shares'" @click="openShare"><Plus /> {{ c.newShare }}</Button>
        <Button v-else @click="openLink"><Plus /> {{ c.newLink }}</Button>
      </template>
      <template #tabs>
        <PageTabs v-model="tab" :items="tabs" label="Shares and links" id-prefix="shares-tab" />
      </template>
    </PageHeader>

    <FilterBar v-model:search="filter" :placeholder="tab === 'shares' ? 'Filter shares…' : 'Filter links…'" :label="tab === 'shares' ? 'Filter shares' : 'Filter links'" class="mb-5" />

    <template v-if="tab === 'shares'">
      <ListSkeleton v-if="shareList.isPending.value" :rows="4" />
      <ErrorState v-else-if="shareList.error.value" :error="shareList.error.value" @retry="shareList.refetch()" />
      <EmptyState v-else-if="!shareList.data.value?.length" :icon="Share2" :title="c.emptyShares" :description="c.emptySharesHint" />
      <DataTable v-else :data="shareList.data.value" :columns="shareColumns" :filter="filter" :row-key="(s) => s.id" />
    </template>

    <template v-else>
      <ListSkeleton v-if="links.isPending.value" :rows="4" />
      <ErrorState v-else-if="links.error.value" :error="links.error.value" @retry="links.refetch()" />
      <EmptyState v-else-if="!links.data.value?.items.length" :icon="Link2" :title="c.emptyLinks" :description="c.emptyLinksHint" />
      <DataTable v-else :data="links.data.value.items" :columns="linkColumns" :filter="filter" :row-key="(l) => l.id" />
    </template>

    <!-- New share -->
    <Dialog v-model:open="shareOpen">
      <DialogContent class="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{{ c.newShare }}</DialogTitle>
          <DialogDescription>{{ c.audienceHint[shareForm.audience] }}</DialogDescription>
        </DialogHeader>
        <form class="grid gap-4" @submit.prevent="submitShare">
          <div class="grid gap-1.5">
            <Label for="sh-title">{{ c.form.title }}</Label>
            <Input id="sh-title" v-model="shareForm.title" required maxlength="200" autofocus />
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="grid gap-1.5">
              <Label for="sh-format">{{ c.form.format }}</Label>
              <SimpleSelect id="sh-format" :model-value="shareForm.format" :options="formatOptions" @update:model-value="(v) => v && (shareForm.format = v as ShareFormat)" />
            </div>
            <div class="grid gap-1.5">
              <Label for="sh-audience">{{ c.form.audience }}</Label>
              <SimpleSelect id="sh-audience" :model-value="shareForm.audience" :options="audienceOptions" @update:model-value="(v) => v && (shareForm.audience = v as ShareAudience)" />
            </div>
          </div>
          <div class="grid gap-1.5">
            <Label for="sh-content">{{ c.form.content }}</Label>
            <Textarea id="sh-content" v-model="shareForm.content" rows="8" required class="font-mono text-xs" />
          </div>
          <div class="grid gap-4 sm:grid-cols-3">
            <div v-if="shareForm.audience !== 'smake'" class="grid gap-1.5">
              <Label for="sh-password">{{ c.form.password }} <span class="font-normal text-muted-foreground">{{ c.form.optional }}</span></Label>
              <Input id="sh-password" v-model="shareForm.password" type="password" autocomplete="new-password" minlength="8" maxlength="200" />
              <p class="text-xs text-muted-foreground">{{ c.form.passwordHint }}</p>
            </div>
            <div class="grid content-start gap-1.5">
              <Label for="sh-expires">{{ c.form.expires }}</Label>
              <Input id="sh-expires" v-model="shareForm.expiresInDays" type="number" min="1" max="90" />
              <p class="text-xs text-muted-foreground">{{ c.form.expiresHint }}</p>
            </div>
            <div class="grid content-start gap-1.5">
              <Label for="sh-views">{{ c.form.maxViews }} <span class="font-normal text-muted-foreground">{{ c.form.optional }}</span></Label>
              <Input id="sh-views" v-model="shareForm.maxViews" type="number" min="1" max="10000" />
            </div>
          </div>
          <FormError :error="shareError" />
          <DialogFooter>
            <Button type="button" variant="ghost" @click="shareOpen = false">{{ t.common.cancel }}</Button>
            <Button type="submit" :disabled="busy || !shareForm.title.trim() || !shareForm.content">{{ t.common.create }}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <!-- Created: link + the password, once -->
    <Dialog v-model:open="createdOpen">
      <DialogContent class="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{{ c.passwordTitle }}</DialogTitle>
          <DialogDescription>{{ created ? c.audienceHint[created.share.audience] : "" }}</DialogDescription>
        </DialogHeader>
        <div v-if="created" class="grid gap-3">
          <CopyField :value="created.share.url" label="link" />
          <template v-if="created.password">
            <CopyField :value="created.password" label="password" />
            <p role="alert" class="rounded-md border border-warning/50 bg-warning-soft/60 p-3 text-sm">{{ c.passwordOnce }}</p>
          </template>
        </div>
        <DialogFooter>
          <Button type="button" @click="created = null">{{ c.done }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- New short link -->
    <Dialog v-model:open="linkOpen">
      <DialogContent class="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{{ c.newLink }}</DialogTitle>
          <DialogDescription>{{ c.form.urlHint(links.data.value?.allowedHosts ?? []) }}</DialogDescription>
        </DialogHeader>
        <form class="grid gap-4" @submit.prevent="submitLink">
          <div class="grid gap-1.5">
            <Label for="ln-url">{{ c.form.url }}</Label>
            <Input id="ln-url" v-model="linkForm.url" type="url" required placeholder="https://" autofocus />
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="grid content-start gap-1.5">
              <Label for="ln-slug">{{ c.form.slug }} <span class="font-normal text-muted-foreground">{{ c.form.optional }}</span></Label>
              <Input id="ln-slug" v-model="linkForm.slug" pattern="[A-Za-z0-9][A-Za-z0-9_-]{2,63}" />
              <p class="text-xs text-muted-foreground">{{ c.form.slugHint }}</p>
            </div>
            <div class="grid content-start gap-1.5">
              <Label for="ln-expires">{{ c.form.linkExpires }}</Label>
              <Input id="ln-expires" v-model="linkForm.expiresInDays" type="number" min="1" max="365" />
              <p class="text-xs text-muted-foreground">{{ c.form.linkExpiresHint }}</p>
            </div>
          </div>
          <div class="grid gap-1.5">
            <Label for="ln-title">{{ c.form.title }} <span class="font-normal text-muted-foreground">{{ c.form.optional }}</span></Label>
            <Input id="ln-title" v-model="linkForm.title" maxlength="200" />
          </div>
          <FormError :error="linkError" />
          <DialogFooter>
            <Button type="button" variant="ghost" @click="linkOpen = false">{{ t.common.cancel }}</Button>
            <Button type="submit" :disabled="busy || !linkForm.url.trim()">{{ t.common.create }}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </div>
</template>
